/**
 * Woobles Universal Utils
 * Lightweight shared helpers only.
 * No cart logic, no GTM/cart attributes, no experiment tracking.
 */

(function () {
  'use strict';

  const WBLS_UTILS_DEBUG = false;

  const log = (...args) => WBLS_UTILS_DEBUG && console.log('[WBLS utils]', ...args);
  const warn = (...args) => WBLS_UTILS_DEBUG && console.warn('[WBLS utils]', ...args);
  const err = (...args) => console.error('[WBLS utils]', ...args);

  /* ---------------------------------------------
   * Cookies
   * ------------------------------------------- */

  const Cookie = {
    get(name) {
      try {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift() || null;
        return null;
      } catch (e) {
        err('Error reading cookie:', name, e?.message || e);
        return null;
      }
    },

    set(name, value, days, opts = {}) {
      try {
        const d = new Date();
        d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);

        const expires = `expires=${d.toUTCString()}`;
        const path = opts.path || '/';
        const domain = opts.domain ? `;domain=${opts.domain}` : '';
        const secure = opts.secure ? ';secure' : '';
        const sameSite = opts.sameSite ? `;samesite=${opts.sameSite}` : '';

        document.cookie = `${name}=${value};${expires};path=${path}${domain}${secure}${sameSite}`;
      } catch (e) {
        err('Error setting cookie:', name, e?.message || e);
      }
    },

    del(name, path = '/', domain) {
      try {
        if (!Cookie.get(name)) return;

        document.cookie =
          `${name}=;` + `path=${path};` + (domain ? `domain=${domain};` : '') + `expires=Thu, 01 Jan 1970 00:00:01 GMT`;
      } catch (e) {
        err('Error deleting cookie:', name, e?.message || e);
      }
    },
  };

  const getCookie = (name) => Cookie.get(name);
  const setCookie = (name, value, expdays) => Cookie.set(name, value, expdays, { path: '/' });
  const delCookie = (name, path, domain) => Cookie.del(name, path, domain);

  /* ---------------------------------------------
   * URL params
   * ------------------------------------------- */

  const getParam = (name, optSearch) => {
    optSearch = optSearch || location.search;
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(optSearch);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  /* ---------------------------------------------
   * Timing helpers
   * ------------------------------------------- */

  const requestAnimFrame = (function () {
    return (
      window.requestAnimationFrame ||
      window.webkitRequestAnimationFrame ||
      window.mozRequestAnimationFrame ||
      function (callback) {
        window.setTimeout(callback, 1000 / 60);
      }
    );
  })();

  function waitUntil(test) {
    return new Promise(function (resolve) {
      (function poll() {
        if (!test()) return requestAnimFrame(poll);
        return resolve();
      })();
    });
  }

  /* ---------------------------------------------
   * DOM observers
   * ------------------------------------------- */

  function waitForElement(selector) {
    return new Promise(function (resolve) {
      const el = document.querySelector(selector);
      if (el) return resolve(el);

      const observer = new MutationObserver(function (mutations) {
        for (let i = 0; i < mutations.length; i++) {
          const nodes = mutations[i].addedNodes;
          if (!nodes || !nodes.length) continue;

          for (let j = 0; j < nodes.length; j++) {
            const node = nodes[j];

            if (node && node.matches && node.matches(selector)) {
              observer.disconnect();
              return resolve(node);
            }

            if (node && node.querySelector) {
              const found = node.querySelector(selector);
              if (found) {
                observer.disconnect();
                return resolve(found);
              }
            }
          }
        }
      });

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    });
  }

  function observeSelector(
    selector,
    callback,
    options = { timeout: null, once: false, onTimeout: null, document: window.document },
  ) {
    let processed = new WeakSet();

    if (options.timeout || options.onTimeout) {
      warn('observeSelector: timeout/onTimeout options exist but are not implemented.');
    }

    let obs;
    let isDone = false;

    const done = () => {
      if (obs) obs.disconnect();
      processed = null;
      obs = null;
      isDone = true;
      return true;
    };

    const processElement = (el) => {
      if (!processed || processed.has(el)) return false;
      processed.add(el);
      callback(el);

      if (options.once) return done();
      return false;
    };

    const lookForSelector = (root = document) => {
      if (!root) return false;

      if (root.matches && root.matches(selector)) {
        if (processElement(root)) return true;
      }

      if (root.querySelectorAll) {
        const els = root.querySelectorAll(selector);
        for (let i = 0; i < els.length; i++) {
          if (processElement(els[i])) return true;
        }
      }

      return false;
    };

    lookForSelector();

    if (!isDone) {
      obs = new MutationObserver((records) => {
        for (let r = 0; r < records.length; r++) {
          const record = records[r];
          const added = record.addedNodes;
          if (!added || !added.length) continue;

          for (let i = 0; i < added.length; i++) {
            const node = added[i];

            if (node && lookForSelector(node)) return;
            if (node && node.parentElement && lookForSelector(node.parentElement)) return;
          }
        }
      });

      obs.observe(options.document || document, {
        attributes: false,
        childList: true,
        subtree: true,
      });
    }

    return () => done();
  }

  /* ---------------------------------------------
   * Script loader
   * ------------------------------------------- */

  function js_loadScript(url, optCallback) {
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = url;

    if (typeof optCallback === 'function') {
      s.onload = optCallback;
    }

    const first = document.getElementsByTagName('script')[0];
    first.parentNode.insertBefore(s, first);
  }

  /* ---------------------------------------------
   * Public API
   * ------------------------------------------- */

  window.WooblesUtils = {
    Cookie,
    getCookie,
    setCookie,
    delCookie,
    getParam,
    waitUntil,
    waitForElement,
    observeSelector,
    js_loadScript,
  };

  // Legacy globals expected by older theme/account scripts.
  window.getCookie = window.getCookie || getCookie;
  window.setCookie = window.setCookie || setCookie;
  window.delCookie = window.delCookie || delCookie;
  window.getParam = window.getParam || getParam;
  window.waitUntil = window.waitUntil || waitUntil;
  window.waitForElement = window.waitForElement || waitForElement;
  window.observeSelector = window.observeSelector || observeSelector;
  window.js_loadScript = window.js_loadScript || js_loadScript;
})();
